/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import ProjectIcon from '@atlaskit/icon/core/project';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import IconTile from '@atlaskit/icon/icon-tile';
import Image from '@atlaskit/image';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import Tile from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	type ActionKeys,
	JIRA_PLANS_CREATE_WORK_ITEM_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { JiraPlansCreateWorkItemPayload } from '@atlassian/conversation-assistant-message-actions-api/jira';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { EntityCard } from '../../common/ui/entity-card';
import { EntityTitle } from '../../common/ui/entity-title';
import { EntityValueGrid, EntityValueGridRow } from '../../common/ui/entity-value-grid';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';
import { jiraPlansActionData } from '../../common/utils/plan-action-data';

import messages from './messages';

const INTERACTION_SOURCE = 'jiraPlansCreateWorkItemRenderer';

const styles = cssMap({
	footer: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: token('space.100'),
	},
});

export const filterCreateWorkItemActions = (
	actions: MessageAction[],
): ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansCreateWorkItem]>[] => {
	const filteredActions = actions.filter((action) => assertIsCreateWorkItemAction(action));
	return filteredActions as ActionWith<
		ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansCreateWorkItem]
	>[];
};

const assertIsCreateWorkItemAction = (action: MessageAction): MessageAction => {
	if (
		!jiraPlansActionData.isPlanActionKeyForRenderer(action.key, JIRA_PLANS_CREATE_WORK_ITEM_KEY)
	) {
		throw new Error('Invalid action key for CreateWorkItemAction');
	}
	return action;
};

export const CreateWorkItemAction = (
	props: ActionRendererFor<typeof ActionKeys.JiraPlansCreateWorkItem>,
): JSX.Element | null => {
	const { actions } = props;

	const firstAction = useMemo(() => filterCreateWorkItemActions(actions)[0], [actions]);
	if (!firstAction) {
		return null;
	}

	return <CreateWorkItemActionContent {...props} firstAction={firstAction} />;
};

const CreateWorkItemActionContent = (
	props: ActionRendererFor<typeof ActionKeys.JiraPlansCreateWorkItem> & {
		firstAction: ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansCreateWorkItem]>;
	},
) => {
	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	const {
		getStatus,
		getReadonly,
		firstAction,
		onConfirm,
		onCancel,
		actions,
		sendMessageConfirmedTools,
		recipientAgentId,
		recipientAgentNamedId,
		conversationId,
		messageId,
	} = props;

	const isCustomAgent = !isDefaultRovoChat({
		agentId: recipientAgentId,
		agentNamedId: recipientAgentNamedId,
	});

	const { data } = firstAction;
	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);
	const invocationStatus = String(status ?? '').toLowerCase();
	const actionStatus = String(firstAction.status ?? '').toLowerCase();

	const isActionLoading = invocationStatus !== 'idle';
	const isSubmitting = invocationStatus === 'started';
	const isActionFinished = invocationStatus === 'finished' || actionStatus === 'finished';
	const buttonsDisabled = isActionLoading || readonly;

	const actionData = jiraPlansActionData.getPlanActionData<JiraPlansCreateWorkItemPayload>(data);

	const handleConfirm = useCallback(() => {
		sendAnalyticsEvent({
			actionSubjectId: firstAction.actionId,
			action: 'clicked',
			actionSubject: 'button',
			attributes: {
				actionSubjectId: 'rovo-chat-jira-plans-create-work-item-confirm',
				planId: actionData.planId,
				scenarioId: actionData.scenarioId,
				summary: actionData.summary,
			},
		});

		if (
			fg('jira_plans_rovo_tools_resumption_migration') ||
			(isCustomAgent && fg('enable_skills_ga_tools_in_studio'))
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: { interactionSource: INTERACTION_SOURCE },
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) =>
						action.invocationId === firstAction.invocationId
							? buildToolFromAction(action, 'confirmed')
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
			return;
		}

		onConfirm(firstAction);
	}, [
		firstAction,
		actions,
		actionData,
		getStatus,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		sendAnalyticsEvent,
		onConfirm,
		isCustomAgent,
	]);

	const handleCancel = useCallback(() => {
		sendAnalyticsEvent({
			actionSubjectId: firstAction.actionId,
			action: 'clicked',
			actionSubject: 'button',
			attributes: {
				actionSubjectId: 'rovo-chat-jira-plans-create-work-item-cancel',
				planId: actionData.planId,
				scenarioId: actionData.scenarioId,
			},
		});

		if (
			fg('jira_plans_rovo_tools_resumption_migration') ||
			(isCustomAgent && fg('enable_skills_ga_tools_in_studio'))
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: { interactionSource: INTERACTION_SOURCE },
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) => buildToolFromAction(action, 'cancelled')),
				},
			});
			return;
		}

		onCancel(firstAction);
	}, [
		firstAction,
		actions,
		actionData,
		getStatus,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		sendAnalyticsEvent,
		onCancel,
		isCustomAgent,
	]);

	const icon = useMemo(
		() =>
			actionData.workTypeIconUrl ? (
				<Tile label="" size="small">
					<Image src={actionData.workTypeIconUrl} alt="" />
				</Tile>
			) : (
				<IconTile label="" appearance="gray" size="small" icon={WorkItemIcon} />
			),
		[actionData.workTypeIconUrl],
	);

	const space = useMemo(
		() => (
			<Inline space="space.100">
				{actionData.sourceAvatarUrl ? (
					<Tile label="" size="xsmall" isInset={false}>
						<Image src={actionData.sourceAvatarUrl} alt="" />
					</Tile>
				) : (
					<IconTile label="" appearance="gray" size="xsmall" icon={ProjectIcon} />
				)}
				<Text maxLines={1}>{actionData.sourceName}</Text>
			</Inline>
		),
		[actionData.sourceAvatarUrl, actionData.sourceName],
	);

	if (!actionData.summary || !actionData.sourceName) {
		return null;
	}

	return (
		<ActionCardWrapper isExpandable title={formatMessage(messages.cardTitle)} product="plan">
			<EntityCard>
				<EntityTitle icon={icon} title={actionData.summary} />
				<EntityValueGrid>
					<EntityValueGridRow label={`${formatMessage(messages.spaceLabel)}:`} value={space} />
					{actionData.priority && (
						<EntityValueGridRow
							label={`${formatMessage(messages.priorityLabel)}:`}
							value={actionData.priority}
						/>
					)}
					{actionData.assigneeName && (
						<EntityValueGridRow
							label={`${formatMessage(messages.assigneeLabel)}:`}
							value={actionData.assigneeName}
						/>
					)}
				</EntityValueGrid>
			</EntityCard>

			{!isActionFinished && (
				<ActionCardFooter>
					<Box xcss={styles.footer}>
						<Button appearance="default" onClick={handleCancel} isDisabled={buttonsDisabled}>
							{formatMessage(messages.cancelButton)}
						</Button>
						<Button
							appearance="primary"
							onClick={handleConfirm}
							isDisabled={buttonsDisabled}
							isLoading={isSubmitting}
						>
							{formatMessage(messages.confirmButton)}
						</Button>
					</Box>
				</ActionCardFooter>
			)}
		</ActionCardWrapper>
	);
};
